/**
 * Pure geometry helpers for seating-canvas table layout.
 *
 * No React, no DnD, no Firebase — safe to import from any file without
 * triggering the react-refresh/only-export-components ESLint rule.
 */

import type { Table } from "@amazing-wedding/shared-types";
import {
  applyLabelMovement,
  labelBoxesOverlap,
  MAX_LAYOUT_PASSES,
  mergeLayoutStateIntoSlot,
  separateOverlappingAABBs,
  type BBox,
  type LayoutState,
} from "./label-collision";

export { labelBoxesOverlap } from "./label-collision";
export type { BBox } from "./label-collision";

// ─── Real-world sizing ────────────────────────────────────────────────────────

/**
 * Real-world table sizing constants.
 *
 * Each seat needs ~60 cm of comfortable circumference at the table edge.
 * Dividing by π gives the diameter for round/oval tables; halving gives the
 * length per long side for rectangular ones.
 */
export const SEAT_SPACING_M = 0.7; // 60 cm comfortable arc per seat
export const TABLE_DEPTH_M = 1.31; // standard table depth (width) for rectangular
export const MIN_TABLE_PX = 46; // ensure tables stay clickable at extreme zoom
/** Round table plate diameter multiplier (visual sizing only). */
export const ROUND_TABLE_SCALE = 1.15;
/** Square table plate side multiplier (visual sizing only). */
export const SQUARE_TABLE_SCALE = 1.25;

/**
 * Derive plate dimensions in CSS pixels from the table's real-world footprint.
 *
 * All formulae are derived from the "60 cm per seat circumference" dining
 * standard (comfortable setting).  Results are then multiplied by
 * `pixelsPerMeter` to convert to canvas pixels.
 *
 * Round (and base of Oval): circumference = capacity × 0.6 m  →  d = circ / π
 * Oval:        width = round diameter, height = width × 0.65
 * Rectangular: each long side seats ceil(capacity/2) guests at 0.6 m each;
 *              table depth is a fixed 0.76 m (standard trestle)
 * Square:      all four sides share the seats equally at 0.6 m each
 */
export function plateSize(
  capacity: number,
  shape: Table["shape"],
  pixelsPerMeter: number,
): { width: number; height: number } {
  if (shape === "round") {
    const diamM = Math.max(0.75, (capacity * SEAT_SPACING_M) / Math.PI);
    const px = Math.max(MIN_TABLE_PX, diamM * pixelsPerMeter * ROUND_TABLE_SCALE);
    return { width: px, height: px };
  }
  if (shape === "oval") {
    const diamM = Math.max(0.75, (capacity * SEAT_SPACING_M) / Math.PI);
    const px = Math.max(MIN_TABLE_PX, diamM * pixelsPerMeter);
    return { width: px, height: Math.max(MIN_TABLE_PX * 0.65, diamM * 0.65 * pixelsPerMeter) };
  }
  if (shape === "rectangular") {
    const seatsPerSide = Math.ceil(capacity / 2);
    const lenM = Math.max(0.9, seatsPerSide * SEAT_SPACING_M);
    return {
      width: Math.max(MIN_TABLE_PX, lenM * pixelsPerMeter),
      height: Math.max(MIN_TABLE_PX * 0.6, TABLE_DEPTH_M * pixelsPerMeter),
    };
  }
  // square — seats distributed across all four sides equally
  const sideM = Math.max(0.75, (capacity / 4) * SEAT_SPACING_M);
  const px = Math.max(MIN_TABLE_PX, sideM * pixelsPerMeter * SQUARE_TABLE_SCALE);
  return { width: px, height: px };
}

// ─── Seat-slot geometry ───────────────────────────────────────────────────────

/** Table rim → dot centre (negative = pip sits slightly inside the rim). */
export const DOT_OFFSET = 11;

/** Half of `DOT_SIZE` in TableShape.tsx — pip radius in canvas pixels. */
export const PIP_RADIUS = 18;

/** Default gap from pip outer edge to label (rectangular long sides, two-line names). */
export const LABEL_GAP_SIDE_RECT = 0;

/** Gap from pip outer edge to label on rectangular / square top & bottom long edges. */
export const LABEL_GAP_END_RECT = 0;

/** Round table — tighter side and end spacing. */
export const LABEL_GAP_SIDE_ROUND = 1;
export const LABEL_GAP_END_ROUND = 0;

/** Oval — between round and rectangular. */
export const LABEL_GAP_SIDE_OVAL = 2;
export const LABEL_GAP_END_OVAL = 0;

/** Label typography — must match SeatLabel CSS. */
export const LABEL_FONT_SIZE = 9.5;
export const LABEL_LINE_HEIGHT = 1.2;
export const LABEL_FONT = '9.5px "Geist Mono", "JetBrains Mono", ui-monospace, monospace';
export const AVG_CHAR_WIDTH = 5.7;

export const MIN_LABEL_WIDTH = 24;
export const MAX_LABEL_WIDTH = 60;

export type LabelOffsets = { side: number; end: number };

/** Dot centre → pip outer edge (PIP_RADIUS), then add shape-specific gap to the label anchor. */
export function labelOffsetsForShape(shape: Table["shape"]): LabelOffsets {
  const pip = PIP_RADIUS;
  switch (shape) {
    case "round":
      return {
        side: pip + LABEL_GAP_SIDE_ROUND,
        end: pip + LABEL_GAP_END_ROUND,
      };
    case "oval":
      return {
        side: pip + LABEL_GAP_SIDE_OVAL,
        end: pip + LABEL_GAP_END_OVAL,
      };
    default:
      return {
        side: pip + LABEL_GAP_SIDE_RECT,
        end: pip + LABEL_GAP_END_RECT,
      };
  }
}

/** @deprecated Use labelOffsetsForShape — kept for tests referencing legacy names. */
export const LABEL_OFFSET_FROM_DOT = PIP_RADIUS + LABEL_GAP_END_RECT;

/** @deprecated Use labelOffsetsForShape — kept for tests referencing legacy names. */
export const LABEL_OFFSET_HORIZONTAL = PIP_RADIUS + LABEL_GAP_SIDE_RECT;

export type LabelSpec = { firstName: string; lastName?: string } | null;

export type SeatSlot = {
  dotX: number;
  dotY: number;
  labelX: number;
  labelY: number;
  align: "left" | "right" | "center";
  /** CSS transform that anchors the label's nearest edge toward the pip. */
  labelTransform: string;
  /** Counter-rotation to apply to the label text so it stays horizontal on rotated tables. */
  rotation?: number;
  /** Unit vector along the table rim / edge (counter-clockwise on round tables). */
  tangentX: number;
  tangentY: number;
  /** Outward normal from table toward the label. */
  radialX: number;
  radialY: number;
  /** Max resting width for the surname line (and layout bbox). */
  maxLabelWidth: number;
  /** Max tangent slide applied by the collision resolver. */
  maxSlide: number;
  /** Tangent offset applied by layoutSeatLabels (already baked into labelX/Y). */
  labelSlide?: number;
  /** Extra outward radial push applied by layoutSeatLabels (already baked in). */
  labelRadialExtra?: number;
};

type Cardinal = "right" | "left" | "top" | "bottom";

/** Unit vector, or upward default at the origin. */
export function normalize(x: number, y: number): { nx: number; ny: number } {
  const len = Math.hypot(x, y);
  if (len < 1e-9) return { nx: 0, ny: -1 };
  return { nx: x / len, ny: y / len };
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

let measureCtx: CanvasRenderingContext2D | null | undefined;

function getMeasureCtx(): CanvasRenderingContext2D | null {
  if (measureCtx !== undefined) return measureCtx;
  if (typeof document === "undefined") {
    measureCtx = null;
    return null;
  }
  const canvas = document.createElement("canvas");
  measureCtx = canvas.getContext("2d");
  return measureCtx;
}

/** Measure text width using canvas (falls back to char estimate in Node). */
export function measureTextWidth(text: string): number {
  const ctx = getMeasureCtx();
  if (ctx) {
    ctx.font = LABEL_FONT;
    return ctx.measureText(text).width;
  }
  return text.length * AVG_CHAR_WIDTH;
}

export function measureLabelBox(
  label: { firstName: string; lastName?: string },
  maxLabelWidth: number,
): { boxW: number; boxH: number } {
  const firstW = measureTextWidth(label.firstName || "?");
  const lastW = label.lastName ? Math.min(measureTextWidth(label.lastName), maxLabelWidth) : 0;
  const boxW = Math.max(firstW, lastW, 1);
  const boxH = LABEL_FONT_SIZE * LABEL_LINE_HEIGHT * (label.lastName ? 2 : 1);
  return { boxW, boxH };
}

// ─── Plate label typography (table name + capacity counter) ─────────────────

/** Base name size at zoom 100 — must match TableShapeBody CSS. */
export const PLATE_NAME_FONT_SIZE = 14;
/** Base counter size at zoom 100. */
export const PLATE_COUNTER_FONT_SIZE = 9;
/** Inner padding on the table plate (matches TableShapeBody). */
export const PLATE_LABEL_PADDING = 4;
export const PLATE_NAME_FONT_MIN = 7;
export const PLATE_COUNTER_FONT_MIN = 5;
export const PLATE_NAME_LINE_HEIGHT = 1.1;
export const PLATE_COUNTER_MARGIN_TOP = 2;

/** Display font stack — aligned with `--font-display` in tokens.css. */
export const PLATE_NAME_FONT_FAMILY =
  '"Instrument Serif", "Cormorant Garamond", "Times New Roman", serif';
/** Mono stack for the capacity counter — aligned with `.t-mono`. */
export const PLATE_COUNTER_FONT_FAMILY = '"Geist Mono", "JetBrains Mono", ui-monospace, monospace';

/** Fallback char width at PLATE_NAME_FONT_SIZE when canvas measure is unavailable. */
export const PLATE_NAME_AVG_CHAR_WIDTH = 8.2;
/** Fallback char width at PLATE_COUNTER_FONT_SIZE. */
export const PLATE_COUNTER_AVG_CHAR_WIDTH = 5.7;

function plateNameFont(fontSize: number): string {
  return `400 ${fontSize}px ${PLATE_NAME_FONT_FAMILY}`;
}

function plateCounterFont(fontSize: number): string {
  return `${fontSize}px ${PLATE_COUNTER_FONT_FAMILY}`;
}

/** Measure plate name width at a given font size (canvas or char estimate). */
export function measurePlateNameWidth(text: string, fontSize: number): number {
  const ctx = getMeasureCtx();
  if (ctx) {
    ctx.font = plateNameFont(fontSize);
    return ctx.measureText(text).width;
  }
  return text.length * PLATE_NAME_AVG_CHAR_WIDTH * (fontSize / PLATE_NAME_FONT_SIZE);
}

/** Measure capacity counter width at a given font size. */
export function measurePlateCounterWidth(text: string, fontSize: number): number {
  const ctx = getMeasureCtx();
  if (ctx) {
    ctx.font = plateCounterFont(fontSize);
    return ctx.measureText(text).width;
  }
  return text.length * PLATE_COUNTER_AVG_CHAR_WIDTH * (fontSize / PLATE_COUNTER_FONT_SIZE);
}

export type PlateLabelTypography = {
  nameFontSize: number;
  counterFontSize: number;
  useEllipsis: boolean;
};

function plateLabelInnerBounds(
  plateWidth: number,
  plateHeight: number,
  shape: Table["shape"],
): { maxW: number; maxH: number } {
  const innerW = plateWidth - PLATE_LABEL_PADDING * 2;
  const innerH = plateHeight - PLATE_LABEL_PADDING * 2;
  const isRound = shape === "round" || shape === "oval";
  if (isRound) {
    const side = Math.min(innerW, innerH) * 0.85;
    return { maxW: side, maxH: side };
  }
  return { maxW: innerW * 0.92, maxH: innerH * 0.85 };
}

function plateLabelFits(
  name: string,
  counterText: string,
  nameFontSize: number,
  maxW: number,
  maxH: number,
): boolean {
  const counterFontSize = nameFontSize * (PLATE_COUNTER_FONT_SIZE / PLATE_NAME_FONT_SIZE);
  const nameW = measurePlateNameWidth(name, nameFontSize);
  const counterW = measurePlateCounterWidth(counterText, counterFontSize);
  const blockH = nameFontSize * PLATE_NAME_LINE_HEIGHT + PLATE_COUNTER_MARGIN_TOP + counterFontSize;
  return nameW <= maxW && counterW <= maxW && blockH <= maxH;
}

/**
 * Zoom-aware plate label sizes: counter-scales when zoomed out for screen
 * readability, then shrinks to fit the plate without wrap or circular clipping.
 */
export function computePlateLabelTypography(params: {
  name: string;
  filled: number;
  capacity: number;
  plateWidth: number;
  plateHeight: number;
  shape: Table["shape"];
  zoom?: number;
}): PlateLabelTypography {
  const { name, filled, capacity, plateWidth, plateHeight, shape } = params;
  const zoom = params.zoom ?? 100;
  const counterText = `${filled} / ${capacity}`;
  const { maxW, maxH } = plateLabelInnerBounds(plateWidth, plateHeight, shape);

  let nameFontSize = zoom >= 100 ? PLATE_NAME_FONT_SIZE : PLATE_NAME_FONT_SIZE * (100 / zoom);

  while (
    nameFontSize > PLATE_NAME_FONT_MIN &&
    !plateLabelFits(name, counterText, nameFontSize, maxW, maxH)
  ) {
    nameFontSize -= 0.5;
  }

  nameFontSize = Math.max(PLATE_NAME_FONT_MIN, nameFontSize);
  const counterFontSize = Math.max(
    PLATE_COUNTER_FONT_MIN,
    nameFontSize * (PLATE_COUNTER_FONT_SIZE / PLATE_NAME_FONT_SIZE),
  );

  const useEllipsis = !plateLabelFits(name, counterText, nameFontSize, maxW, maxH);

  return {
    nameFontSize: Math.round(nameFontSize * 10) / 10,
    counterFontSize: Math.round(counterFontSize * 10) / 10,
    useEllipsis,
  };
}

/** Zoom at/above which mobile canvas shows full seat name labels (else pip initials). */
export const MOBILE_SEAT_FULL_LABEL_ZOOM = 72;

export type SeatGuestDisplayMode = "fullLabels" | "pipInitials";

export function resolveSeatGuestDisplayMode(
  zoom: number,
  isMobile: boolean,
  tableSelected: boolean,
): SeatGuestDisplayMode {
  if (!isMobile || tableSelected) return "fullLabels";
  return zoom >= MOBILE_SEAT_FULL_LABEL_ZOOM ? "fullLabels" : "pipInitials";
}

/**
 * Outward normal on the seat ellipse (round / oval rim + DOT_OFFSET).
 * Gradient of (x/a)² + (y/b)² = 1 at the dot position.
 */
export function ellipseOutwardNormal(
  dotX: number,
  dotY: number,
  aDot: number,
  bDot: number,
): { nx: number; ny: number } {
  return normalize(dotX / (aDot * aDot), dotY / (bDot * bDot));
}

/** CCW tangent on the seat ellipse at the dot. */
export function ellipseTangent(
  dotX: number,
  dotY: number,
  aDot: number,
  bDot: number,
): { tx: number; ty: number } {
  const sinA = dotY / bDot;
  const cosA = dotX / aDot;
  const { nx, ny } = normalize(-aDot * sinA, bDot * cosA);
  return { tx: nx, ty: ny };
}

/** Snap a normal to the nearest cardinal for horizontal label anchoring (rect tables). */
function dominantCardinal(nx: number, ny: number): Cardinal {
  if (Math.abs(nx) > Math.abs(ny) * 1.15) return nx > 0 ? "right" : "left";
  if (Math.abs(ny) > Math.abs(nx) * 1.15) return ny < 0 ? "top" : "bottom";
  if (Math.abs(nx) >= Math.abs(ny)) return nx > 0 ? "right" : "left";
  return ny < 0 ? "top" : "bottom";
}

function offsetForCardinal(cardinal: Cardinal, offsets: LabelOffsets): number {
  return cardinal === "left" || cardinal === "right" ? offsets.side : offsets.end;
}

/** Unit step along the snapped cardinal (matches CSS anchor direction). */
function cardinalUnit(cardinal: Cardinal): { ux: number; uy: number } {
  switch (cardinal) {
    case "right":
      return { ux: 1, uy: 0 };
    case "left":
      return { ux: -1, uy: 0 };
    case "top":
      return { ux: 0, uy: -1 };
    case "bottom":
      return { ux: 0, uy: 1 };
  }
}

function anchorFieldsFromCardinal(cardinal: Cardinal): Pick<SeatSlot, "align" | "labelTransform"> {
  switch (cardinal) {
    case "right":
      return { align: "left", labelTransform: "translate(0, -50%)" };
    case "left":
      return { align: "left", labelTransform: "translate(-100%, -50%)" };
    case "top":
      return { align: "center", labelTransform: "translate(-50%, -100%)" };
    case "bottom":
      return { align: "center", labelTransform: "translate(-50%, 0%)" };
  }
}

/** Sector anchoring for radial round/oval labels (45° wedges, horizontal text). */
function anchorFieldsFromRadialNormal(
  nx: number,
  ny: number,
): Pick<SeatSlot, "align" | "labelTransform"> {
  if (Math.abs(nx) > Math.abs(ny)) {
    return nx > 0
      ? { align: "left", labelTransform: "translate(0, -50%)" }
      : { align: "left", labelTransform: "translate(-100%, -50%)" };
  }
  return ny < 0
    ? { align: "center", labelTransform: "translate(-50%, -100%)" }
    : { align: "center", labelTransform: "translate(-50%, 0%)" };
}

/**
 * Rectangular tables: label anchor along snapped cardinal.
 */
export function labelFieldsFromNormal(
  nx: number,
  ny: number,
  dotX: number,
  dotY: number,
  shape: Table["shape"],
): Pick<SeatSlot, "labelX" | "labelY" | "align" | "labelTransform"> {
  const cardinal = dominantCardinal(nx, ny);
  const { ux, uy } = cardinalUnit(cardinal);
  const { align, labelTransform } = anchorFieldsFromCardinal(cardinal);
  const offset = offsetForCardinal(cardinal, labelOffsetsForShape(shape));
  return {
    labelX: dotX + ux * offset,
    labelY: dotY + uy * offset,
    align,
    labelTransform,
  };
}

/**
 * Round / oval: label anchor along the outward normal (radial fan).
 */
export function labelFieldsFromRadialNormal(
  nx: number,
  ny: number,
  dotX: number,
  dotY: number,
  shape: Table["shape"],
): Pick<SeatSlot, "labelX" | "labelY" | "align" | "labelTransform"> {
  const offset = labelOffsetsForShape(shape).side;
  const { align, labelTransform } = anchorFieldsFromRadialNormal(nx, ny);
  return {
    labelX: dotX + nx * offset,
    labelY: dotY + ny * offset,
    align,
    labelTransform,
  };
}

function roundMaxLabelWidth(
  dotX: number,
  dotY: number,
  radialOffset: number,
  capacity: number,
): number {
  const labelRadius = Math.hypot(dotX, dotY) + radialOffset;
  const chord = 2 * labelRadius * Math.sin(Math.PI / capacity);
  return clamp(chord * 0.9, MIN_LABEL_WIDTH, MAX_LABEL_WIDTH);
}

function rectMaxLabelWidth(edgeLength: number, seatsOnEdge: number): number {
  const pitch = edgeLength / Math.max(1, seatsOnEdge);
  return clamp(pitch * 0.95, MIN_LABEL_WIDTH, MAX_LABEL_WIDTH);
}

/** Compute label anchor bbox in table coordinates. */
export function labelAnchorBBox(
  slot: SeatSlot,
  boxW: number,
  boxH: number,
  slide = 0,
  radialExtra = 0,
): BBox {
  const ax = slot.labelX + slide * slot.tangentX + radialExtra * slot.radialX;
  const ay = slot.labelY + slide * slot.tangentY + radialExtra * slot.radialY;
  const tf = slot.labelTransform;

  if (tf === "translate(0, -50%)") {
    return { l: ax, r: ax + boxW, t: ay - boxH / 2, b: ay + boxH / 2 };
  }
  if (tf === "translate(-100%, -50%)") {
    return { l: ax - boxW, r: ax, t: ay - boxH / 2, b: ay + boxH / 2 };
  }
  if (tf === "translate(-50%, -100%)") {
    return { l: ax - boxW / 2, r: ax + boxW / 2, t: ay - boxH, b: ay };
  }
  // translate(-50%, 0%) or legacy translateX(-50%)
  return { l: ax - boxW / 2, r: ax + boxW / 2, t: ay, b: ay + boxH };
}

const VERTICAL_STAGGER = 4;

function layoutBBox(slot: SeatSlot, state: LayoutState): BBox {
  return labelAnchorBBox(slot, state.boxW, state.boxH, state.slide, state.radialExtra);
}

/**
 * Resolve label collisions: tangent nudge then radial push (D3 pie-chart pattern).
 * Returns new slots with adjusted labelX/labelY and layout metadata.
 */
export function layoutSeatLabels(
  slots: SeatSlot[],
  labels: LabelSpec[],
  _shape: Table["shape"],
): SeatSlot[] {
  const hasLabels = labels.some((l) => l !== null);
  if (!hasLabels) return slots;

  const states: LayoutState[] = slots.map((slot, i) => {
    const label = labels[i] ?? null;
    if (!label) return { slide: 0, radialExtra: 0, boxW: 0, boxH: 0 };
    const { boxW, boxH } = measureLabelBox(label, slot.maxLabelWidth);
    return { slide: 0, radialExtra: 0, boxW, boxH };
  });

  for (let pass = 0; pass < MAX_LAYOUT_PASSES; pass++) {
    let moved = false;

    for (let i = 0; i < slots.length; i++) {
      for (let j = i + 1; j < slots.length; j++) {
        const si = states[i]!;
        const sj = states[j]!;
        if (si.boxW <= 0 || sj.boxW <= 0) continue;

        const slotI = slots[i]!;
        const slotJ = slots[j]!;
        const boxI = layoutBBox(slotI, si);
        const boxJ = layoutBBox(slotJ, sj);

        if (!labelBoxesOverlap(boxI, boxJ)) continue;
        if (
          separateOverlappingAABBs(
            boxI,
            boxJ,
            (dx: number, dy: number) => applyLabelMovement(slotI, si, dx, dy),
            (dx: number, dy: number) => applyLabelMovement(slotJ, sj, dx, dy),
          )
        ) {
          moved = true;
        }
      }
    }

    if (!moved) break;
  }

  // Rectangular long sides: alternate vertical stagger when boxes still overlap vertically
  for (let i = 0; i < slots.length; i++) {
    const slot = slots[i]!;
    const isSide = Math.abs(slot.radialX) > 0.9 && Math.abs(slot.tangentY) > 0.9;
    if (!isSide) continue;
    const si = states[i]!;
    if (si.boxW <= 0) continue;
    // Odd seats nudge slightly along radial (tiny vertical visual stagger on sides)
    if (i % 2 === 1) {
      si.radialExtra += VERTICAL_STAGGER * 0.15;
    }
  }

  return slots.map((slot, i) => mergeLayoutStateIntoSlot(slot, states[i]!));
}

/**
 * Compute seat slot positions for a table.  Pure geometry — no React, no DnD.
 */
export function buildSeatSlots(
  capacity: number,
  widthPx: number,
  heightPx: number,
  shape: Table["shape"],
  tableRotation: number,
): SeatSlot[] {
  const slots: SeatSlot[] = [];
  const round = shape === "round" || shape === "oval";

  if (round) {
    const aDot = widthPx / 2 + DOT_OFFSET;
    const bDot = heightPx / 2 + DOT_OFFSET;
    const radialOffset = labelOffsetsForShape(shape).side;

    for (let i = 0; i < capacity; i++) {
      const angle = (i / capacity) * Math.PI * 2 - Math.PI / 2;
      const dotX = Math.cos(angle) * aDot;
      const dotY = Math.sin(angle) * bDot;
      const { nx, ny } = ellipseOutwardNormal(dotX, dotY, aDot, bDot);
      const { tx, ty } = ellipseTangent(dotX, dotY, aDot, bDot);
      const maxLabelWidth = roundMaxLabelWidth(dotX, dotY, radialOffset, capacity);
      const chord = 2 * (Math.hypot(dotX, dotY) + radialOffset) * Math.sin(Math.PI / capacity);
      const maxSlide = chord * 0.4;

      slots.push({
        dotX,
        dotY,
        ...labelFieldsFromRadialNormal(nx, ny, dotX, dotY, shape),
        tangentX: tx,
        tangentY: ty,
        radialX: nx,
        radialY: ny,
        maxLabelWidth,
        maxSlide,
      });
    }
  } else {
    const sideLengths = [widthPx, heightPx, widthPx, heightPx];
    const perimeter = sideLengths.reduce((a, b) => a + b, 0);
    const counts = sideLengths.map((s) => Math.floor((capacity * s) / perimeter));
    let rem = capacity - counts.reduce((a, b) => a + b, 0);
    const fracs = sideLengths
      .map((s, i) => ({ i, frac: (capacity * s) / perimeter - (counts[i] ?? 0) }))
      .sort((a, b) => b.frac - a.frac);
    for (const { i } of fracs) {
      if (rem <= 0) break;
      counts[i] = (counts[i] ?? 0) + 1;
      rem--;
    }
    const [topN = 0, rightN = 0, bottomN = 0, leftN = 0] = counts;
    const rot = tableRotation;

    const pushEdge = (
      count: number,
      nx: number,
      ny: number,
      edgeLength: number,
      tangentX: number,
      tangentY: number,
      dotAt: (t: number) => { dotX: number; dotY: number },
    ) => {
      const maxLabelWidth = rectMaxLabelWidth(edgeLength, count);
      const maxSlide = (edgeLength / Math.max(1, count)) * 0.4;

      for (let i = 0; i < count; i++) {
        const t = (i + 0.5) / count;
        const { dotX, dotY } = dotAt(t);
        slots.push({
          dotX,
          dotY,
          ...labelFieldsFromNormal(nx, ny, dotX, dotY, shape),
          tangentX,
          tangentY,
          radialX: nx,
          radialY: ny,
          maxLabelWidth,
          maxSlide,
          rotation: rot,
        });
      }
    };

    pushEdge(topN, 0, -1, widthPx, 1, 0, (t) => ({
      dotX: t * widthPx - widthPx / 2,
      dotY: -heightPx / 2 - DOT_OFFSET,
    }));
    pushEdge(rightN, 1, 0, heightPx, 0, 1, (t) => ({
      dotX: widthPx / 2 + DOT_OFFSET,
      dotY: t * heightPx - heightPx / 2,
    }));
    pushEdge(bottomN, 0, 1, widthPx, 1, 0, (t) => ({
      dotX: t * widthPx - widthPx / 2,
      dotY: heightPx / 2 + DOT_OFFSET,
    }));
    pushEdge(leftN, -1, 0, heightPx, 0, 1, (t) => ({
      dotX: -widthPx / 2 - DOT_OFFSET,
      dotY: t * heightPx - heightPx / 2,
    }));
  }

  return slots;
}

/**
 * Compute seat slot positions without rendering.  Extracted so the
 * interactive seat-preview in the Add Table popover can reuse the geometry.
 */
export function computeSeatSlots(
  capacity: number,
  shape: Table["shape"],
  pixelsPerMeter: number,
): { widthPx: number; heightPx: number; slots: SeatSlot[] } {
  const { width: widthPx, height: heightPx } = plateSize(capacity, shape, pixelsPerMeter);
  const slots = buildSeatSlots(capacity, widthPx, heightPx, shape, 0);
  return { widthPx, heightPx, slots };
}
